import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './DemoMode.css';

const features = [
  {
    id: 'home',
    title: 'Home Dashboard',
    description: 'Careem-style super app interface with quick access to all services',
    path: '/',
    icon: '🏠',
    category: 'Core',
    duration: 5000,
    highlights: ['Quick service tiles', 'One-tap booking', 'Personalized recommendations']
  },
  {
    id: 'rides',
    title: 'Ride Pooling',
    description: 'Find and share rides with fellow students - airport runs, daily commutes, campus rides',
    path: '/rides',
    icon: '🚗',
    category: 'Transportation',
    duration: 6000,
    highlights: ['Airport shuttles', 'Daily commutes to NYC', 'Cost sharing']
  },
  {
    id: 'marketplace',
    title: 'Marketplace',
    description: 'Buy & sell textbooks, electronics, furniture and more from verified students',
    path: '/marketplace',
    icon: '🛍️',
    category: 'Commerce',
    duration: 5000,
    highlights: ['Textbooks', 'Electronics', 'Furniture', 'Student-to-student']
  },
  {
    id: 'rentals',
    title: 'Rentals',
    description: 'Rent laptops, cameras, equipment, and more from fellow students',
    path: '/rentals',
    icon: '📦',
    category: 'Commerce',
    duration: 5000,
    highlights: ['MacBooks', 'DSLR cameras', 'Electric scooters', 'Lab equipment']
  },
  {
    id: 'services',
    title: 'Services Hub',
    description: 'Browse all student services - tutoring, errands, campus tasks',
    path: '/services',
    icon: '🎯',
    category: 'Services',
    duration: 5000,
    highlights: ['Tutoring', 'Grocery delivery', 'Laundry service', 'Moving help']
  },
  {
    id: 'college-zone',
    title: 'College Zone',
    description: 'Connect with students across universities - networking made easy',
    path: '/college-zone',
    icon: '🎓',
    category: 'Social',
    duration: 5000,
    highlights: ['Student profiles', 'Multi-university', 'Skills & interests', 'Networking']
  },
  {
    id: 'isa-community',
    title: 'ISA Community',
    description: 'Indian Student Association community hub - events, rides, housing',
    path: '/community/isa',
    icon: '🇮🇳',
    category: 'Community',
    duration: 5000,
    highlights: ['Event posts', 'Ride sharing', 'Housing', 'Community support']
  },
  {
    id: 'feed',
    title: 'OnTap Feed',
    description: 'Social feed for campus updates, events, and student content',
    path: '/feed',
    icon: '📱',
    category: 'Social',
    duration: 5000,
    highlights: ['Campus news', 'Event updates', 'Student posts', 'Engagement']
  },
  {
    id: 'mindwave',
    title: 'MindWave AI Hub',
    description: 'AI-powered tools for students - study assistant, research help, and more',
    path: '/mindwave',
    icon: '🧠',
    category: 'AI Tools',
    duration: 6000,
    highlights: ['Study assistant', 'Research helper', 'Writing support', 'Career advisor']
  },
  {
    id: 'study-assistant',
    title: 'AI Study Assistant',
    description: 'Get AI-powered help with homework, concepts, and exam prep',
    path: '/ai/study-assistant',
    icon: '📚',
    category: 'AI Tools',
    duration: 5000,
    highlights: ['Concept explanations', 'Problem solving', 'Exam prep', 'Flashcards']
  },
  {
    id: 'smart-campus',
    title: 'Smart Campus',
    description: 'AI-powered campus navigation and information assistant',
    path: '/ai/smart-campus',
    icon: '🏛️',
    category: 'AI Tools',
    duration: 5000,
    highlights: ['Building finder', 'Office hours', 'Event calendar', 'Campus info']
  },
  {
    id: 'wellness',
    title: 'Wellness Coach',
    description: 'Mental health support and wellness resources for students',
    path: '/ai/wellness',
    icon: '🧘',
    category: 'AI Tools',
    duration: 5000,
    highlights: ['Stress management', 'Mindfulness', 'Health tips', 'Support resources']
  },
  {
    id: 'translator',
    title: 'AI Translator',
    description: 'Real-time translation for international students',
    path: '/ai/translator',
    icon: '🌍',
    category: 'AI Tools',
    duration: 5000,
    highlights: ['50+ languages', 'Academic terms', 'Real-time', 'Voice support']
  },
  {
    id: 'resume',
    title: 'Resume Optimizer',
    description: 'AI-powered resume builder and optimizer for job applications',
    path: '/tools/resume-optimizer',
    icon: '📄',
    category: 'Career',
    duration: 5000,
    highlights: ['ATS optimization', 'Keyword analysis', 'Industry templates', 'Score feedback']
  },
  {
    id: 'student-tools',
    title: 'Student Tools',
    description: 'Collection of productivity tools for students',
    path: '/student-tools',
    icon: '🛠️',
    category: 'Tools',
    duration: 5000,
    highlights: ['GPA calculator', 'Schedule planner', 'Note taking', 'Task manager']
  }
];

const categories = ['All', 'Core', 'Transportation', 'Commerce', 'Services', 'Social', 'Community', 'AI Tools', 'Career', 'Tools'];

function DemoMode() {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [visitedFeatures, setVisitedFeatures] = useState(new Set());
  const [showOverlay, setShowOverlay] = useState(true);
  const [countdown, setCountdown] = useState(0);

  const filteredFeatures = selectedCategory === 'All'
    ? features
    : features.filter(f => f.category === selectedCategory);

  // Auto-play functionality
  useEffect(() => {
    let timer;
    let countdownTimer;

    if (isAutoPlaying && filteredFeatures.length > 0) {
      const currentFeature = filteredFeatures[currentIndex];
      setCountdown(Math.ceil(currentFeature.duration / 1000));

      // Countdown timer
      countdownTimer = setInterval(() => {
        setCountdown(prev => Math.max(0, prev - 1));
      }, 1000);

      // Navigation timer
      timer = setTimeout(() => {
        if (currentIndex < filteredFeatures.length - 1) {
          setCurrentIndex(currentIndex + 1);
        } else {
          setCurrentIndex(0);
          setIsAutoPlaying(false);
        }
      }, currentFeature.duration);
    }

    return () => {
      clearTimeout(timer);
      clearInterval(countdownTimer);
    };
  }, [isAutoPlaying, currentIndex, filteredFeatures]);

  const handleFeatureClick = (feature, index) => {
    setVisitedFeatures(prev => new Set([...prev, feature.id]));
    setCurrentIndex(index);
  };

  const handleExplore = (feature) => {
    setVisitedFeatures(prev => new Set([...prev, feature.id]));
    navigate(feature.path);
  };

  const startAutoTour = () => {
    setCurrentIndex(0);
    setIsAutoPlaying(true);
    setShowOverlay(false);
  };

  const stopAutoTour = () => {
    setIsAutoPlaying(false);
  };

  const currentFeature = filteredFeatures[currentIndex] || features[0];
  const progress = ((visitedFeatures.size / features.length) * 100).toFixed(0);

  return (
    <div className="demo-mode">
      {/* Welcome Overlay */}
      {showOverlay && (
        <div className="demo-overlay">
          <div className="demo-overlay-content">
            <h1>Welcome to ONTAP-SPU Demo</h1>
            <p className="demo-subtitle">Winner of HackSPU 2025</p>
            <p>Experience the complete campus super-app with {features.length} features</p>

            <div className="demo-start-options">
              <button className="demo-btn primary" onClick={startAutoTour}>
                ▶️ Start Guided Tour
                <span>Auto-navigate through all features</span>
              </button>
              <button className="demo-btn secondary" onClick={() => setShowOverlay(false)}>
                🎮 Explore Manually
                <span>Browse features at your own pace</span>
              </button>
            </div>

            <div className="demo-quick-stats">
              <div className="stat">
                <span className="stat-number">15+</span>
                <span className="stat-label">Features</span>
              </div>
              <div className="stat">
                <span className="stat-number">6</span>
                <span className="stat-label">AI Tools</span>
              </div>
              <div className="stat">
                <span className="stat-number">10</span>
                <span className="stat-label">Demo Accounts</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Demo Interface */}
      <div className="demo-header">
        <div className="demo-header-left">
          <h1>🎯 ONTAP Demo Mode</h1>
          <div className="demo-progress">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }}></div>
            </div>
            <span>{visitedFeatures.size}/{features.length} explored</span>
          </div>
        </div>

        <div className="demo-controls">
          {isAutoPlaying ? (
            <button className="control-btn stop" onClick={stopAutoTour}>
              ⏹️ Stop Tour ({countdown}s)
            </button>
          ) : (
            <button className="control-btn play" onClick={startAutoTour}>
              ▶️ Auto Tour
            </button>
          )}
          <button className="control-btn" onClick={() => navigate('/showcase')}>
            📋 Showcase
          </button>
        </div>
      </div>

      {/* Category Filter */}
      <div className="demo-categories">
        {categories.map(cat => (
          <button
            key={cat}
            className={`category-btn ${selectedCategory === cat ? 'active' : ''}`}
            onClick={() => {
              setSelectedCategory(cat);
              setCurrentIndex(0);
            }}
          >
            {cat}
          </button>
        ))}
      </div>

      <div className="demo-content">
        {/* Feature List Sidebar */}
        <div className="demo-sidebar">
          <h3>Features ({filteredFeatures.length})</h3>
          <div className="feature-list">
            {filteredFeatures.map((feature, index) => (
              <div
                key={feature.id}
                className={`feature-item ${currentIndex === index ? 'active' : ''} ${visitedFeatures.has(feature.id) ? 'visited' : ''}`}
                onClick={() => handleFeatureClick(feature, index)}
              >
                <span className="feature-icon">{feature.icon}</span>
                <div className="feature-info">
                  <span className="feature-title">{feature.title}</span>
                  <span className="feature-category">{feature.category}</span>
                </div>
                {visitedFeatures.has(feature.id) && <span className="visited-check">✓</span>}
              </div>
            ))}
          </div>
        </div>

        {/* Feature Preview */}
        <div className="demo-preview">
          <div className="preview-card">
            <div className="preview-header">
              <span className="preview-icon">{currentFeature.icon}</span>
              <div>
                <h2>{currentFeature.title}</h2>
                <span className="preview-category">{currentFeature.category}</span>
              </div>
            </div>

            <p className="preview-description">{currentFeature.description}</p>

            <div className="preview-highlights">
              <h4>Key Features:</h4>
              <div className="highlights-grid">
                {currentFeature.highlights.map((highlight, i) => (
                  <span key={i} className="highlight-tag">✨ {highlight}</span>
                ))}
              </div>
            </div>

            <div className="preview-actions">
              <button
                className="explore-btn primary"
                onClick={() => handleExplore(currentFeature)}
              >
                🚀 Explore {currentFeature.title}
              </button>
              <button
                className="explore-btn secondary"
                onClick={() => {
                  if (currentIndex < filteredFeatures.length - 1) {
                    setCurrentIndex(currentIndex + 1);
                  } else {
                    setCurrentIndex(0);
                  }
                }}
              >
                Next Feature →
              </button>
            </div>

            <div className="preview-path">
              <code>{currentFeature.path}</code>
            </div>
          </div>

          {/* Quick Access Grid */}
          <div className="quick-access">
            <h3>⚡ Quick Access</h3>
            <div className="quick-grid">
              <button onClick={() => navigate('/rides')} className="quick-btn">
                🚗 Rides
              </button>
              <button onClick={() => navigate('/marketplace')} className="quick-btn">
                🛍️ Market
              </button>
              <button onClick={() => navigate('/services')} className="quick-btn">
                🎯 Services
              </button>
              <button onClick={() => navigate('/college-zone')} className="quick-btn">
                🎓 Connect
              </button>
              <button onClick={() => navigate('/mindwave')} className="quick-btn">
                🧠 AI Hub
              </button>
              <button onClick={() => navigate('/community/isa')} className="quick-btn">
                🇮🇳 ISA
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Demo Login Info */}
      <div className="demo-footer">
        <div className="login-info">
          <strong>Demo Login:</strong> demo1@saintpeters.edu - demo10@saintpeters.edu | Password: <code>demo123</code>
        </div>
        <div className="tech-stack">
          <span>React</span>
          <span>Node.js</span>
          <span>MongoDB</span>
          <span>OpenAI</span>
        </div>
      </div>

      {/* Floating Navigation */}
      {!showOverlay && (
        <div className="floating-nav">
          <button
            onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
            disabled={currentIndex === 0}
          >
            ←
          </button>
          <span>{currentIndex + 1} / {filteredFeatures.length}</span>
          <button
            onClick={() => setCurrentIndex(Math.min(filteredFeatures.length - 1, currentIndex + 1))}
            disabled={currentIndex === filteredFeatures.length - 1}
          >
            →
          </button>
        </div>
      )}
    </div>
  );
}

export default DemoMode;
